import { storeToRefs } from 'pinia';
import type { FormConfig } from '@/common/store/BookStore';
import { useMetadataStore } from '@/widgets/Wizard/stores/metadata';
import type { Account } from '@/widgets/Wizard/models/accounts';

export async function getFormConfig(locale: string): Promise<FormConfig> {
    const metaDataStore = useMetadataStore();
    const { tommyCountries } = storeToRefs(metaDataStore);

    return {
        groups: [
            {
                rows: [
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'select',
                                label: 'book__form_field_salutation',
                                code: 'title',
                                choices: [
                                    { value: 'mv', label: 'book__tommy_salution_malefemale', translate: true },
                                    { value: 'm', label: 'book__tommy_salution_male', translate: true },
                                    { value: 'v', label: 'book__tommy_salution_female', translate: true },
                                    { value: 'fam', label: 'book__tommy_salution_family', translate: true },
                                ],
                                value: 'mv',
                                alias: 'title',
                                enabled: isEnabledField('title'),
                                required: isMandatoryField('title'),
                                forceShow: true,
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_firstname',
                                code: 'first-name',
                                enabled: isEnabledField('name', 'first-name'),
                                required: isMandatoryField('name', 'first-name'),
                                forceShow: true,
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_infix',
                                code: 'infix',
                                classes: ['sm'],
                                enabled: isEnabledField('name', 'last-name.prefix'),
                                required: isMandatoryField('name', 'last-name.prefix'),
                                forceShow: true,
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_surname',
                                code: 'name',
                                alias: 'name',
                                enabled: isEnabledField('name', 'last-name'),
                                required: isMandatoryField('name', 'last-name'),
                                forceShow: true,
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'birthdate',
                                label: 'book__form_field_birthdate',
                                code: 'date-of-birth',
                                alias: 'date-of-birth',
                                placeholder: 'dd-mm-jjjj',
                                enabled: isEnabledField('date-of-birth'),
                                required: isMandatoryField('date-of-birth'),
                                options: {
                                    translate: true,
                                },
                            },
                            {
                                type: 'text',
                                label: 'book__form_field_birthplace',
                                code: 'birth-place',
                                alias: 'birth-place',
                                enabled: isEnabledField('birth-place'),
                                required: isMandatoryField('birth-place'),
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'email',
                                label: 'book__form_field_email',
                                code: 'email',
                                enabled: isEnabledField('email'),
                                required: isMandatoryField('email'),
                                forceShow: true,
                                alias: 'email',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_mobile',
                                code: 'mobile-number',
                                alias: 'mobile-number',
                                enabled: isEnabledField('mobile-number'),
                                required: isMandatoryField('mobile-number'),
                                forceShow: false,
                                options: {
                                    translate: true,
                                },
                            },
                            {
                                type: 'nationality',
                                label: 'book__form_field_nationality',
                                code: 'nationality',
                                alias: 'nationality',
                                choices: tommyCountries.value
                                    .map((country: any) => {
                                        return {
                                            value: country.code,
                                            label: country.name,
                                            translate: false,
                                        };
                                    })
                                    .sort((a: any, b: any) => {
                                        return a.label.localeCompare(b.label);
                                    }),
                                value: 'NL',
                                enabled: isEnabledField('nationality'),
                                required: isMandatoryField('nationality'),
                                forceShow: false,
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_phone',
                                code: 'phone-number',
                                alias: 'phone-number',
                                enabled: isEnabledField('phone-number'),
                                required: isMandatoryField('phone-number'),
                                forceShow: false,
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                ],
            },
            {
                rows: [
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'country',
                                label: 'book__form_field_country',
                                code: 'country',
                                enabled: isEnabledField('address', 'country'),
                                required: isMandatoryField('address', 'country'),
                                alias: 'address',
                                choices: tommyCountries.value
                                    .map((country: any) => {
                                        return {
                                            value: country.code,
                                            label: country.name,
                                            translate: false,
                                        };
                                    })
                                    .sort((a: any, b: any) => {
                                        return a.label.localeCompare(b.label);
                                    }),
                                value: 'NL',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_zipcode',
                                code: 'post-code',
                                enabled: isEnabledField('address', 'post-code'),
                                required: isMandatoryField('address', 'post-code'),
                                classes: ['sm'],
                                alias: 'address',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_house_number',
                                code: 'street-number',
                                enabled: isEnabledField('address', 'street-number'),
                                required: isMandatoryField('address', 'street-number'),
                                classes: ['sm'],
                                alias: 'address',
                                options: {
                                    translate: true,
                                },
                            },
                            {
                                type: 'text',
                                label: 'book__form_field_house_number_suffix',
                                code: 'street-number-suffix',
                                enabled: isEnabledField('address', 'street-number-suffix'),
                                required: isMandatoryField('address', 'street-number-suffix'),
                                classes: ['sm'],
                                alias: 'address',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_street',
                                code: 'street',
                                enabled: isEnabledField('address', 'street'),
                                required: isMandatoryField('address', 'street'),
                                alias: 'address',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_place',
                                code: 'city',
                                enabled: isEnabledField('address', 'city'),
                                required: isMandatoryField('address', 'city'),
                                alias: 'address',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                ],
            },
            {
                rows: [
                    {
                        isDivider: false,
                        fields: [
                            {
                                type: 'text',
                                label: 'book__form_field_passport_number',
                                code: 'passport-number',
                                enabled: isEnabledField('passport-number'),
                                required: isMandatoryField('passport-number'),
                                classes: ['sm'],
                                forceShow: false,
                                alias: 'passport-number',
                                options: {
                                    translate: true,
                                },
                            },
                            {
                                type: 'text',
                                label: 'book__form_field_license_plate',
                                code: 'license-plate',
                                enabled: isEnabledField('license-plate'),
                                required: isMandatoryField('license-plate'),
                                classes: ['sm'],
                                forceShow: false,
                                alias: 'license-plate',
                                options: {
                                    translate: true,
                                },
                            },
                            {
                                type: 'text',
                                label: 'book__form_field_license_plate2',
                                code: 'license-plate2',
                                enabled: isEnabledField('license-plate2'),
                                required: isMandatoryField('license-plate2'),
                                classes: ['sm'],
                                forceShow: false,
                                alias: 'license-plate2',
                                options: {
                                    translate: true,
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    };
}

export function applyFormFieldOverrides(
    formConfig: FormConfig,
    overrides: Record<string, boolean>,
): FormConfig {
    for (const group of formConfig.groups) {
        for (const row of group.rows) {
            for (const field of row.fields) {
                if (Object.prototype.hasOwnProperty.call(overrides, field.code)) {
                    field.enabled = overrides[field.code] ?? false;
                }
            }
        }
    }

    return formConfig;
}

export function applyArrangementFormConfig(
    formConfig: FormConfig,
    arrangementId: string,
    arrangementFormConfig: Account['options']['arrangementFormConfig'],
): FormConfig {
    if (!arrangementFormConfig || !arrangementFormConfig[arrangementId]) {
        return formConfig;
    }

    return applyFormFieldOverrides(formConfig, arrangementFormConfig[arrangementId]);
}

export function isMandatoryField(fieldCode: string, subFieldCode: string | null = null): boolean {
    return isListedInsideTommyFormConfigFieldKey('mandatory', 'mandatory-fields', fieldCode, subFieldCode);
}

export function isEnabledField(fieldCode: string, subFieldCode: string | null = null): boolean {
    return isListedInsideTommyFormConfigFieldKey('enabled', 'enabled-fields', fieldCode, subFieldCode);
}

export function isListedInsideTommyFormConfigFieldKey(
    key: string,
    collectionKey: string,
    fieldCode: string,
    subFieldCode: string | null = null,
): boolean {
    const metaDataStore = useMetadataStore();
    const { tommyMetaData } = storeToRefs(metaDataStore);

    if (!tommyMetaData.value) {
        return false;
    }

    const field: any = tommyMetaData.value.customerFieldSettings.find((field: any) => {
        return field.name === fieldCode;
    });

    if (!field || !field[key]) {
        return false;
    }

    if (Array.isArray(field[collectionKey])) {
        const subFieldKeys = [fieldCode, subFieldCode, subFieldCode?.replace(/\./g, '-')].filter(Boolean);

        return subFieldKeys.some((key) => field[collectionKey].includes(key));
    }

    return false;
}
